import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { fieldWarnings } from '../lib/fieldChecks.ts';
import { Warn } from '../lib/icons.tsx';
import type { EditableField, MediaField } from '../../shared/deck.ts';

/** Blur-to-save handler shared by every field control here. */
type SaveField = (fieldId: string, value: string) => void;

/**
 * Shared Edit-tab field controls — used by both the flat sections in
 * EditPanel.tsx and the grouped item cards in ListEditor.tsx.
 */

/** Shared blur-to-save text state: Escape restores, empty never saves. */
function useFieldValue(field: EditableField, onSave: SaveField) {
  const [value, setValue] = useState(field.value);
  const savedRef = useRef(field.value);

  // External refresh (reload, chat apply, list op) → resync.
  useEffect(() => {
    setValue(field.value);
    savedRef.current = field.value;
  }, [field.value]);

  function commit() {
    const v = value.trim();
    if (!v || v === savedRef.current) {
      if (!v) setValue(savedRef.current); // never save a field to empty
      return;
    }
    savedRef.current = v;
    onSave(field.id, v);
  }
  function revert() {
    setValue(savedRef.current);
  }
  return { value, setValue, commit, revert };
}

/** One editable text field: input for short primitives, auto-growing
 *  textarea for prose. Saves on blur when the value changed; Escape
 *  restores the last saved value. */
export function FieldControl({
  field,
  onSave,
  disabled,
}: {
  field: EditableField;
  onSave: SaveField;
  disabled?: boolean;
}) {
  const { value, setValue, commit, revert } = useFieldValue(field, onSave);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const long =
    field.multiline !== undefined
      ? field.multiline
      : !['Title', 'Kicker', 'SubHead', 'Token', 'CoverTitle', 'BigStat'].includes(field.primitive);
  const warnings = fieldWarnings(field.primitive, value);

  function autoGrow() {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }
  useEffect(() => {
    if (long) autoGrow();
  }, [long, value]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (e.key === 'Escape') {
      revert();
    } else if (e.key === 'Enter' && !long) {
      e.preventDefault();
      if (e.target instanceof HTMLElement) e.target.blur(); // commit via blur
    }
  }

  return (
    <label className="edit-panel__field">
      <span className="edit-panel__field-label">{field.label}</span>
      {long ? (
        <textarea
          ref={taRef}
          className="edit-panel__input"
          rows={2}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={onKeyDown}
          disabled={disabled}
        />
      ) : (
        <input
          className="edit-panel__input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={onKeyDown}
          disabled={disabled}
        />
      )}
      {warnings.map((w, i) => (
        <span key={i} className="edit-panel__warning">
          <Warn className="icon-inline" size={12} /> {w}
        </span>
      ))}
    </label>
  );
}

/** A bare inline input for compact rows and table cells — the field's label
 *  lives in the row/column header, not above the input. */
export function CompactField({ field, onSave, bare }: { field: EditableField; onSave: SaveField; bare?: boolean }) {
  const { value, setValue, commit, revert } = useFieldValue(field, onSave);
  return (
    <input
      className={'edit-panel__input edit-list__input' + (bare ? ' edit-list__input--bare' : '')}
      type="text"
      title={field.label}
      placeholder={field.label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') revert();
        else if (e.key === 'Enter') {
          e.preventDefault();
          if (e.target instanceof HTMLElement) e.target.blur();
        }
      }}
    />
  );
}

/** A styled-text field (nested JSX spans) — read-only here, edit in chat. */
export function LockedField({ field, onAskInChat }: { field: EditableField; onAskInChat?: (prefill: string) => void }) {
  return (
    <div className="edit-panel__field edit-panel__field--locked">
      <span className="edit-panel__field-label">{field.label}</span>
      <p className="edit-panel__locked-value">{field.value}</p>
      <p className="muted small" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Styled text: edit in chat.
        {onAskInChat && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => onAskInChat?.(`Change the ${field.label} text to: `)}
          >
            Edit in chat
          </button>
        )}
      </p>
    </div>
  );
}

/** One media row: thumbnail (or empty-slot box) + filename + Replace/Add.
 *  Deck assets are served by the deck's preview child, so the thumbnail is
 *  the same URL the slide itself renders. */
export function MediaRow({
  slug,
  field,
  disabled,
  onReplace,
}: {
  slug: string;
  field: MediaField;
  disabled?: boolean;
  onReplace?: () => void;
}) {
  // Template placeholder artwork lives in the ENGINE's template assets, not
  // this deck's assets/ — the deck-assets thumbnail URL would 404, so
  // placeholder rows get the icon treatment instead.
  const isAsset =
    field.assetName && !field.placeholder && !field.assetName.startsWith('(') && !/^https?:/.test(field.assetName);
  const empty = !field.assetName;
  return (
    <div
      className="edit-panel__field"
      style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: '0.6rem' }}
    >
      {field.kind === 'image' && isAsset ? (
        <img
          src={`/preview/${slug}/assets/${encodeURIComponent(field.assetName ?? '')}`}
          alt=""
          style={{
            width: 72,
            aspectRatio: '16 / 10',
            objectFit: 'cover',
            borderRadius: 4,
            background: '#111',
            flex: 'none',
          }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            width: 72,
            aspectRatio: '16 / 10',
            borderRadius: 4,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            fontSize: empty ? 11 : 20,
            background: empty ? 'repeating-linear-gradient(135deg, #1a1a1a 0 6px, #101010 6px 12px)' : '#000',
            border: '1px solid var(--line, #444)',
            color: 'var(--fg-3, #888)',
          }}
        >
          {empty ? 'empty' : field.kind === 'video' ? '🎞' : '🖼'}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="edit-panel__field-label" style={{ display: 'block' }}>
          {field.label}
        </span>
        <span
          className="muted small"
          style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {empty ? 'No media yet' : field.placeholder ? `${field.assetName} (placeholder)` : field.assetName}
        </span>
      </span>
      <button
        type="button"
        className={empty ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
        onClick={onReplace}
        disabled={disabled}
      >
        {empty ? 'Add…' : 'Replace…'}
      </button>
    </div>
  );
}
